// Iter-237 — Shared category-rubric registry.
//
// Pulls the per-vertical CompactRubricStrip factor lists into one
// module so the home-page <InsuranceComparisonTable> AND the
// /auto-insurance landing page can render the SAME strip without
// duplicating the factor metadata. New verticals (auto-loan, warranty,
// refinance) can opt in by adding their entry here.
//
// `.jsx` extension because the factor objects carry lucide-react icon
// component references (JSX).

import {
  DollarSign,
  ShieldCheck,
  Award,
  ClipboardCheck,
  Percent,
  Sliders,
  Zap,
  Wrench,
  Layers,
} from "lucide-react";

export const INSURANCE_RUBRIC = [
  {
    Icon: DollarSign,
    title: "Premium pricing",
    weight: "35%",
    testid: "insurance-rubric-premium-pricing",
    explainer:
      "Weighted highest because it is the only factor that directly translates into monthly cash savings.",
  },
  {
    Icon: ShieldCheck,
    title: "Coverage & claims",
    weight: "30%",
    testid: "insurance-rubric-coverage-claims",
    explainer:
      "Payout speed + dispute rates from NAIC + AM Best data — cheap premiums mean nothing if claims stall.",
  },
  {
    Icon: Award,
    title: "Customer reviews",
    weight: "20%",
    testid: "insurance-rubric-customer-reviews",
    explainer:
      "Verified-buyer satisfaction from JD Power + Trustpilot — controls for support quality across 100k+ policies.",
  },
  {
    Icon: ClipboardCheck,
    title: "Discounts & tools",
    weight: "15%",
    testid: "insurance-rubric-discounts-tools",
    explainer:
      "Telematics, multi-policy, and good-driver discount depth — where most drivers leave money on the table.",
  },
];

export const INSURANCE_RUBRIC_CONTEXT =
  "Premium pricing and claims reputation carry the most weight because they're the two factors that materially change your bottom line — your monthly check, and what gets paid out when something goes wrong. The table mixes direct carriers we audit (Progressive, GEICO, State Farm, Allstate, USAA, Nationwide, Farmers, and Liberty Mutual) with leading insurance marketplaces (The Zebra, LendingTree Insurance, and Insurify) so you can compare apples-to-apples direct quotes against marketplace-shopped quotes in one view.";

// ─── Iter-238 — Auto loan rubric (was inline in ComparisonTable.jsx) ──
export const AUTO_LOAN_RUBRIC = [
  {
    Icon: Percent,
    title: "Rates & APR",
    weight: "40%",
    testid: "auto-loan-rubric-rates-apr",
    explainer:
      "Weighted highest because APR is the single line item that compounds across 60–84 months of payments.",
  },
  {
    Icon: Sliders,
    title: "Loan terms",
    weight: "25%",
    testid: "auto-loan-rubric-loan-terms",
    explainer:
      "Term range + max LTV + credit floors — controls who actually gets the headline rate vs. who gets repriced.",
  },
  {
    Icon: Zap,
    title: "Application",
    weight: "20%",
    testid: "auto-loan-rubric-application",
    explainer:
      "Soft-pull pre-qualification, time-to-decision, and document workload — most under-rated lender factor.",
  },
  {
    Icon: Wrench,
    title: "Support",
    weight: "15%",
    testid: "auto-loan-rubric-support",
    explainer:
      "Funding speed, post-close servicing reputation, refinance flexibility — where lender quality compounds.",
  },
];

export const AUTO_LOAN_RUBRIC_CONTEXT =
  "Rates and term flexibility carry the most weight because they decide both your monthly payment and whether you can refinance into a better deal later. The table pairs three direct lenders we audit (LightStream, Capital One Auto Navigator, PenFed Credit Union) with two marketplaces (AutoPay, myAutoloan) so you can compare a direct headline rate against a single-application marketplace match.";


// ─── Iter-238 — Warranty rubric (NEW) ─────────────────────────────────
// Mirrors the published methodology at /methodology/warranty. The two
// 10% sub-buckets ("Deductible flexibility" + "Cancel + refund policy")
// are merged into a single 20% "Terms & flexibility" tile so the strip
// renders as a balanced 4-up grid identical to the insurance + auto-loan
// strips. Visible weights still sum to 100 and the full sub-buckets
// remain in /methodology/warranty for readers who want the full
// breakdown.
export const WARRANTY_RUBRIC = [
  {
    Icon: ShieldCheck,
    title: "Coverage breadth",
    weight: "35%",
    testid: "warranty-rubric-coverage-breadth",
    explainer:
      "Named components + depth of the exclusion list + whether wear-and-tear is covered. 'All-but-named' plans outscore 'named-component-only' plans.",
  },
  {
    Icon: DollarSign,
    title: "Cost vs. repair data",
    weight: "25%",
    testid: "warranty-rubric-cost-vs-repair",
    explainer:
      "Plan price as a multiple of the mean 5-year out-of-warranty repair cost. Above 2.0× scores lower; below 1.2× scores higher.",
  },
  {
    Icon: Award,
    title: "Claims record",
    weight: "20%",
    testid: "warranty-rubric-claims-record",
    explainer:
      "BBB rating + complaint resolution rate + consumer report data on denials. Providers <1 year old or with a B- BBB or lower are flagged.",
  },
  {
    Icon: Layers,
    title: "Terms & flexibility",
    weight: "20%",
    testid: "warranty-rubric-terms-flexibility",
    explainer:
      "Deductible tiers + transferability + refund window + cancellation structure. Plans with 30+ day full-refund windows score higher.",
  },
];

export const WARRANTY_RUBRIC_CONTEXT =
  "Coverage and cost-vs-repair-data carry the most weight because vehicle service contracts only pay off when the plan covers the parts most likely to break before the math beats the sticker price. The table pairs direct administrators we audit (Endurance, CARCHEX, CarShield, Olive, autopom!) so you can compare named-component plans against all-but-named plans before shopping for a quote.";
